import { decode } from "html-entities";

/**
 * A studio's positioning: the six rich-text fields an admin writes on the
 * Positioning page, read one way and written into prompts one way.
 *
 * About twenty-five generators each selected these fields and typed out their
 * own "Label: value" block. The copies drifted: three fed archived personas
 * into prompts, three read Workspace columns that do not exist (so every hint
 * said "Not specified"), the target-audience field went by seven names, and
 * only one stripped the editor's HTML before the model saw it.
 *
 * What a generator puts around the block (a heading, the homepage, its own
 * scenarios) stays with the generator; which fields it prints and what a
 * blank field says are options here, not copies.
 *
 * No database here, so a prompt builder that only formats can import it; the
 * read is `getStudioPositioning` in ./studio-positioning.js.
 */

const NOT_SPECIFIED = "Not specified";

/** The positioning fields, in the order prompts list them, with their one label. */
export const POSITIONING_FIELDS = Object.freeze([
  ["valueProp", "Value Proposition"],
  ["companyValues", "Company Values"],
  ["productMarketCategory", "Product Market Category"],
  ["whoCaresALot", "Ideal Customers and Key Stakeholders"],
  ["competitiveAlternatives", "Competitive Alternatives"],
  ["uniqueAttributes", "Unique Attributes"],
]);

export const POSITIONING_FIELD_KEYS = Object.freeze(
  POSITIONING_FIELDS.map(([key]) => key),
);

export const POSITIONING_FIELD_LABELS = Object.freeze(
  Object.fromEntries(POSITIONING_FIELDS),
);

/** The studio columns a positioning block reads. */
export const STUDIO_POSITIONING_SELECT = Object.freeze({
  id: true,
  name: true,
  homePageURL: true,
  ...Object.fromEntries(POSITIONING_FIELD_KEYS.map((key) => [key, true])),
});

/**
 * A positioning field as plain prompt text: the Positioning page saves the
 * editor's HTML, which is noise to a model.
 */
export function positioningText(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/<\/(p|div|section|h[1-6])>/gi, "\n\n")
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    // Every named and numeric entity the editor can paste (&apos;, &#8217;…),
    // with a non-breaking space read as a space.
    .replace(/&[#\w]+;/g, (entity) => decode(entity).replace(/\u00a0/g, " "))
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function clip(text, maxLength) {
  if (!maxLength || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * The studio's positioning as "Label: value" lines, or "" when there is no
 * studio (or nothing to say and `blank` is null).
 *
 * @param {object|null} workspace  a row read with STUDIO_POSITIONING_SELECT,
 *   or any object carrying the positioning keys
 * @param {object} [options]
 * @param {string} [options.bullet]  prefix for every line ("- " or "")
 * @param {string|null} [options.blank]  what a blank field says; null leaves
 *   its line out
 * @param {string|false} [options.company]  label for the studio name line
 * @param {string|false} [options.website]  label for the homepage line
 * @param {string[]} [options.fields]  which fields, in order
 * @param {Record<string,string>} [options.labels]  a generator's own label
 *   for a field, where its prompt refers to that label by name
 * @param {number|null} [options.maxLength]  per-field character cap
 */
export function formatStudioPositioning(
  workspace,
  {
    bullet = "",
    blank = NOT_SPECIFIED,
    company = "Company",
    website = false,
    fields = POSITIONING_FIELD_KEYS,
    labels = {},
    maxLength = null,
  } = {},
) {
  if (!workspace) return "";

  const line = (label, raw) => {
    const text = clip(positioningText(raw), maxLength);
    if (text) return `${bullet}${label}: ${text}`;
    return blank === null ? null : `${bullet}${label}: ${blank}`;
  };

  return [
    company ? line(company, workspace.name) : null,
    ...fields.map((key) =>
      line(labels[key] || POSITIONING_FIELD_LABELS[key] || key, workspace[key]),
    ),
    website ? line(website, workspace.homePageURL) : null,
  ]
    .filter(Boolean)
    .join("\n");
}
